import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, Check } from "lucide-react";
import useClickOutside from "@/hooks/useClickOutSide";

interface Option {
  id: string;
  label: string;
}

interface CustomSelectFieldProps {
  options: Option[];
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  dropdownClassName?: string;
  showSearch?: boolean;
  disabledOptions?: string[];
  showClearIcon?: boolean;
}

const CustomSelectField: React.FC<CustomSelectFieldProps> = ({
  options,
  placeholder = "Select field",
  value,
  onChange,
  className = "",
  dropdownClassName = "",
  showSearch = true,
  disabledOptions = [],
  showClearIcon = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useClickOutside(() => setIsOpen(false));
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((option) => option.id === value);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [options, searchTerm]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (index: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.2,
      },
    }),
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Select Button */}
      <motion.button
        type="button"
        onClick={handleToggle}
        whileTap={{ scale: 0.98 }}
        className={`w-full p-2 border rounded-md text-sm bg-white flex items-center justify-between outline-none ${
          isOpen ? "border-gray-400" : "border-fieldBorder"
        }`}
      >
        <span className={`truncate text-left ${selectedOption ? "text-text-default" : "text-gray-500"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <div className="flex items-center gap-1">
          {selectedOption && showClearIcon && (
            <motion.button
              type="button"
              onClick={handleClear}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-0.5 hover:bg-gray-100 rounded-full"
            >
              <X className="w-3 h-3 text-gray-400" />
            </motion.button>
          )}
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </motion.div>
        </div>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg ${dropdownClassName}`}
          >
            {/* Search Input */}
            {showSearch && (
              <div className="p-2 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 text-sm border border-fieldBorder rounded focus:border-gray-400 outline-none"
                />
              </div>
            )}

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                <div className="py-1">
                  {filteredOptions.map((option, index) => {
                    const isSelected = option.id === value;
                    const isDisabled = disabledOptions.includes(option.id);
                    return (
                      <motion.button
                        key={option.id}
                        type="button"
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                        onClick={() => !isDisabled && handleSelect(option.id)}
                        whileHover={!isDisabled ? { backgroundColor: "#f3f4f6" } : {}}
                        whileTap={!isDisabled ? { scale: 0.98 } : {}}
                        disabled={isDisabled}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between ${
                          isDisabled
                            ? "text-gray-400 cursor-not-allowed bg-gray-50"
                            : isSelected
                            ? "bg-blue-50 text-blue-600 hover:bg-blue-100"
                            : "text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{option.label}</span>
                        {isSelected && !isDisabled && (
                          <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.2 }}>
                            <Check className="w-4 h-4 text-blue-600" />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-gray-500">No options found</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomSelectField;
